import { FileStats, IDBManagerDexie } from './IDBManagerDexie';
import { type MinetestConsole } from './GlobalContext';
import { zip, unzip, type Unzipped } from 'fflate';
import dayjs from 'dayjs';

export interface StorageStats {
  fileCount: number;
  totalSize: number;
  lastUpdate: Date | null;
}

export class StorageManager {
  private idbManager: IDBManagerDexie | null = null;
  private storagePolicy: string = 'no-storage';
  private _isInitialized: boolean | Promise<boolean> = false;
  private _hasCopiedToModuleFS: boolean = false;
  private statsChangeListeners: Set<((stats: StorageStats) => void)> = new Set();
  
  private worldsStats: StorageStats = {
    fileCount: 0,
    totalSize: 0,
    lastUpdate: null
  };
  
  private modsStats: StorageStats = {
    fileCount: 0,
    totalSize: 0,
    lastUpdate: null
  };

  private minetestConsole!: MinetestConsole;
  private activeWorld: string | null = null;
  private trackedWorlds: string[] = [];
  private worldSyncTimeout: any = undefined;
  private worldNamesToSync: Set<string> = new Set();
  private fileStats: Map<string, FileStats> = new Map();
  
  
  private readonly WORLDS_SYNC_BASE_PATH = '/minetest/worlds';
  private readonly MODS_SYNC_BASE_PATH = '/minetest/mods';
  // Cache de media. NO se persiste (cargar miles de archivos en cada relaunch rompía el
  // join). Se conserva la constante solo para PURGAR de IndexedDB lo que haya quedado de
  // intentos anteriores y mantener el boot liviano (ver copyIdbPathToModuleFS).
  private readonly CACHE_SYNC_BASE_PATH = '/minetest/cache/media/';

  public autoSync: boolean = true;
  // PiolaCraft: antes era 120_000 (120s). Como el debounce se reseteaba con CADA
  // cambio de archivo y durante el juego activo (mapgen/ABM/movimiento) los archivos
  // cambian sin parar, el timer NUNCA llegaba a dispararse → el mundo no se guardaba
  // a IndexedDB y se regeneraba en cada arranque. Ahora: debounce corto + max-wait
  // (ver scheduleIDBFlush) para persistir cada ~30s aunque haya actividad continua.
  public autoSyncDebounceDelay: number = 10_000;
  public autoSyncMaxWait: number = 30_000;

  constructor() {
    // Empty constructor - initialization happens with initialize() 
  }
  
  // Return the current storage policy
  getStoragePolicy(): string {
    return this.storagePolicy;
  }
  
  // Initialize storage manager
  async initialize(storageOptions: { policy: string }, minetestConsole: MinetestConsole): Promise<void> {
    this._isInitialized = new Promise<boolean>(async (resolve, reject) => {
      this.minetestConsole = minetestConsole;
      if (this.storagePolicy === storageOptions.policy) {
        this.minetestConsole.print('StorageManager: Already initialized with identical policy');
        resolve(true);
        return;
      }
  
      this.storagePolicy = storageOptions.policy;
      this.minetestConsole.print(`StorageManager: Initializing with policy - ${storageOptions.policy}`);
  
      if (storageOptions.policy === 'indexeddb') {
        try {
          if (!this.idbManager) {
            const idbManagerInstance = new IDBManagerDexie();
            await idbManagerInstance.initDB();
            this.idbManager = idbManagerInstance;
            this.minetestConsole.print('StorageManager: IndexedDB initialized for persistence.');
          }
          this.updateStorageStats();
        } catch (e) {
          console.error('StorageManager: Failed to initialize IndexedDB backend.', e);
          this.minetestConsole.printErr('StorageManager: Failed to initialize IndexedDB backend.');
          this.storagePolicy = 'no-storage';
          reject(e);
          return;
        }
      }
      resolve(true);
    });
    await this._isInitialized;
  }

  // Load files from IndexedDB to the filesystem
  private async copyIdbPathToModuleFS(): Promise<void> {
    if (!window.Module?.FS || !this.idbManager) {
      console.error('StorageManager-IDB: Module.FS or IDBManager not available for initial load.');
      return Promise.reject('FS or IDBManager not ready for IDB load');
    }
    
    console.log('StorageManager-IDB: Starting initial load from IndexedDB');
    try {
      // PiolaCraft: el cache de media ya NO se persiste (cargar miles de archivos en
      // cada relaunch desestabilizaba el join → "Fatal error in FS.readFile"). Purgamos
      // lo que haya quedado de intentos anteriores para volver a un boot liviano.
      try { await this.idbManager.deleteAllByPath(this.CACHE_SYNC_BASE_PATH); } catch (e) { /* */ }
      const files = await this.idbManager.getAllFiles();
      if (files.length === 0) {
        console.log('StorageManager-IDB: No files found in IndexedDB.');
        return;
      }
      console.log(`StorageManager-IDB: Found ${files.length} file(s) to load into WasmFS.`);

      for (const file of files) {
        try {
          // Set stats
          this.fileStats.set(file.path, file.stats);

          // Create directory if it doesn't exist
          const dir = file.path.substring(0, file.path.lastIndexOf('/'));
          if (dir && !window.Module.FS.analyzePath(dir).exists) {
            window.Module.FS.mkdirTree(dir);
          }
          
          // Ensure content is properly typed for writing to filesystem
          if (file.content) {
            const content = file.content instanceof Uint8Array ? 
              file.content : 
              new Uint8Array(file.content);
              
            window.Module.FS.writeFile(file.path, content);
          } else {
            console.log(`StorageManager-IDB: Empty content for file ${file.path}, skipping`);
          }
        } catch (e) {
          console.error('StorageManager-IDB: Error writing file to WasmFS:', file.path, e);
        }
      }
      this.updateStorageStats();
      console.log('StorageManager-IDB: Finished loading files from IndexedDB into WasmFS.');
    } catch (e) {
      console.error('StorageManager-IDB: Error during initial load from IndexedDB:', e);
      throw e; 
    }
  }

  // Update storage statistics
  private updateStorageStats(): void {
    try {
      // Get all worlds files from IndexedDB
      const worldFiles = Array.from(this.fileStats.entries());
      let worldsSize = 0;
      
      // Calculate total size of world files
      for (const file of worldFiles) {
        const isDirectory = (file[1].mode & 0x4000) === 0x4000;
        if (isDirectory) {
          continue;
        }
        worldsSize += file[1].size;
      }
      
      this.worldsStats = {
        fileCount: worldFiles.length,
        totalSize: worldsSize,
        lastUpdate: new Date()
      };
    } catch (e) {
      console.error('StorageManager: Error calculating IndexedDB stats:', e);
    }
    this.notifyStatsChange();
  }

  private alwaysSync: string[] = [
    '/minetest/minetest.conf',
    '/minetest/client/mod_storage.sqlite',
  ];

  private pendingIndexedDBWrites: Map<string, { data: Uint8Array | null, stats: FileStats | null }> = new Map();
  private idbFlushTimeout?: NodeJS.Timeout;
  private firstPendingAt: number = 0;

  private scheduleIDBFlush(): void {
    const now = Date.now();
    if (this.firstPendingAt === 0) this.firstPendingAt = now;
    if (this.idbFlushTimeout) clearTimeout(this.idbFlushTimeout);
    // Debounce normal, PERO con techo (max-wait): si hay escrituras pendientes hace
    // demasiado, forzamos el flush aunque sigan llegando cambios. Así el mundo se
    // guarda cada ~30s durante el juego activo en vez de nunca.
    const elapsed = now - this.firstPendingAt;
    const delay = Math.max(0, Math.min(this.autoSyncDebounceDelay, this.autoSyncMaxWait - elapsed));
    this.idbFlushTimeout = setTimeout(() => { this.flushIDBWrites(); }, delay);
  }

  private async flushIDBWrites(): Promise<void> {
    this.firstPendingAt = 0;
    if (!this.idbManager || this.pendingIndexedDBWrites.size === 0) return;
    const writes = Array.from(this.pendingIndexedDBWrites.entries());
    this.pendingIndexedDBWrites.clear();

    for (const [path, info] of writes) {
      try {
        if (info.stats === null) {
          await this.idbManager.deleteFile(path);
        } else if (info.data === null) {
          await this.idbManager.storeDirectory(path);
        } else {
          await this.idbManager.saveFile(path, info.data, info.stats);
        }
      } catch (e) {
        console.error(`StorageManager: Error flushing to IDB for ${path}:`, e);
      }
    }
    this.updateStorageStats();
  }

  public async executeWorldSync(): Promise<void> {
    await this.flushIDBWrites();
  }

  private scheduleWorldSync(worldName?: string): void {
    // Stubbed out for compatibility. The new sync uses scheduleIDBFlush().
  }

  get isInitialized(): boolean | Promise<boolean> {
    return this._isInitialized;
  }

  get hasCopiedToModuleFS(): boolean {
    return this._hasCopiedToModuleFS;
  }

  private worldSyncInProgress: boolean = false;
  private nextWorldSyncTime: number = 0;

  get worldSyncInfo(): { nextSync: number | null, inProgress: boolean } {
    return {
      nextSync: this.nextWorldSyncTime > 0 ? this.nextWorldSyncTime : null,
      inProgress: this.worldSyncInProgress,
    };
  }

  setMinetestConsole(minetestConsole: MinetestConsole): void {
    this.minetestConsole = minetestConsole;
  }

  addStatsChangeListener(listener: (stats: StorageStats) => void): void {
    this.statsChangeListeners.add(listener);
  }

  removeStatsChangeListener(listener: (stats: StorageStats) => void): void {
    this.statsChangeListeners.delete(listener);
  }

  private notifyStatsChange(): void {
    const stats = this.getStats();
    for (const listener of Array.from(this.statsChangeListeners)) {
      listener(stats);
    }
  }

  public getStats(): StorageStats {
    let lastUpdate: Date | null = null;
    if (this.worldsStats.lastUpdate && this.modsStats.lastUpdate) {
      lastUpdate = this.worldsStats.lastUpdate > this.modsStats.lastUpdate ? this.worldsStats.lastUpdate : this.modsStats.lastUpdate;
    }
    else if (this.worldsStats.lastUpdate) {
      lastUpdate = this.worldsStats.lastUpdate;
    }
    else if (this.modsStats.lastUpdate) {
      lastUpdate = this.modsStats.lastUpdate;
    }
    const stats: StorageStats = {
      fileCount: this.worldsStats.fileCount + this.modsStats.fileCount,
      totalSize: this.worldsStats.totalSize + this.modsStats.totalSize,
      lastUpdate,
    };
    return stats;
  }

  public fileChanged(filePath: string): void {
    if (this.storagePolicy !== 'indexeddb') return;
    const path = filePath.replace(/\/[^\/]+\/\.\.\//g, '/');
    if (path.startsWith('/minetest/worlds/') || path.startsWith('/minetest/mods/') || this.alwaysSync.includes(path)) {
      try {
        if (!window.Module?.FS) return;
        const stats = window.Module.FS.stat(path);
        const isDirectory = (stats.mode & 0x4000) === 0x4000;
        
        if (!isDirectory) {
          const data = window.Module.FS.readFile(path, { encoding: 'binary' });
          this.fileStats.set(path, stats);
          this.pendingIndexedDBWrites.set(path, { data, stats });
        } else {
          this.fileStats.set(path, stats);
          this.pendingIndexedDBWrites.set(path, { data: null, stats });
        }
        
        this.scheduleIDBFlush();
      } catch (e) {
      }
    }
  }

  public fileDeleted(filePath: string): void {
    if (this.storagePolicy !== 'indexeddb') return;
    const path = filePath.replace(/\/[^\/]+\/\.\.\//g, '/');
    if (path.startsWith('/minetest/worlds/') || path.startsWith('/minetest/mods/') || this.alwaysSync.includes(path)) {
      this.pendingIndexedDBWrites.set(path, { data: null, stats: null });
      this.scheduleIDBFlush();
    }
  }

  async copyToModuleFS(): Promise<void> {
    if (!window.Module?.FS) {
      console.error('StorageManager: Module.FS is not available for initialization.');
      return Promise.reject('Module.FS not available');
    }

    if (this._hasCopiedToModuleFS) {
      console.log('StorageManager: Module.FS has already been copied to WasmFS, skipping.');
      return;
    }

    this._hasCopiedToModuleFS = true;
    // Load worlds and mods data from IndexedDB
    await this.copyIdbPathToModuleFS();
  }

  public async downloadAllFilesAsZip(): Promise<void> {
    if (!this.idbManager) {
      console.error('StorageManager: No IDBManager available for download operation');
      return;
    }
    
    // First, sync all files
    await this.executeWorldSync();

    const files = await this.idbManager.getAllFiles();
    const zipContent: Record<string, Uint8Array> = {};
    for (const file of files) {
      zipContent[file.path] = file.content;
    }
    const zipFile = await new Promise<Uint8Array>((resolve, reject) => {
      zip(
        zipContent,
        {
          level: 9,
          // consume: true,
        },
        (err, data) => {
          if (err) {
            reject(err);
          }
          else {
            resolve(data);
          }
        }
      );
    });
    const zipBlob = new Blob([zipFile], { type: 'application/zip' });
    const url = URL.createObjectURL(zipBlob);
    const a = document.createElement('a');
    a.style.display = 'none';
    document.body.appendChild(a);
    a.href = url;
    a.download = `minetest_storage_${dayjs().format('YYYY-MM-DD_HH-mm')}.zip`;
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  public async restoreFromZip(zipFile: Uint8Array): Promise<void> {
    if (!this.idbManager) {
      console.error('StorageManager: No IDBManager available for restore operation');
      return;
    }
    console.log('StorageManager: Restoring from zip');
    const unzipped = await new Promise<Unzipped>((resolve, reject) => {
      const terminate = unzip(
        zipFile,
        {},
        (err, data) => {
          if (err) {
            reject(err);
          }
          else {
            resolve(data);
          }
        }
      );
    });
    await this.idbManager.clearDatabase();
    this.fileStats.clear();
    const addedDirectories: Set<string> = new Set();
    const now = Math.floor(Date.now() / 1000);
    console.log("UNZIPPED", unzipped);
    for (let file of Object.keys(unzipped)) {
      if (file.endsWith('/')) {
        console.log("Skipping directory", file);
        continue;
      }
      let relativePath: string;
      if (file.startsWith('/minetest/')) {
        relativePath = file.slice('/minetest/'.length);
      }
      else if (file.startsWith('minetest/')) {
        relativePath = file.slice('minetest/'.length);
      }
      else {
        this.minetestConsole.printErr('StorageManager: File does not start with /minetest/ or minetest/: ' + file);
        continue;
      }
      console.log("Restoring file", file);
      const pathFragments = relativePath.split('/');
      for (let i = 1; i < pathFragments.length; i++) {
        const dirPath = '/minetest/' + pathFragments.slice(0, i).join('/');
        if (dirPath !== '/minetest/' && !addedDirectories.has(dirPath)) {
          await this.idbManager.storeDirectory(dirPath);
          addedDirectories.add(dirPath);
        }
      }
      if (!unzipped[file]) {
        this.minetestConsole.printErr('StorageManager: File not found in zip: ' + file);
        continue;
      }
      const stats: FileStats = {
        mtime: now,
        atime: now,
        ctime: now,
        size: unzipped[file].length,
        mode: 0, // only used for directory checks
      };
      this.fileStats.set(file, stats);
      await this.idbManager.saveFile(file, unzipped[file], stats);
    }
    this.updateStorageStats();
  }

  // ── Realms: export/restore ACOTADO a la carpeta de un mundo ──────────────────
  // A diferencia de restoreFromZip (que borra TODO el IndexedDB), esto opera SOLO
  // sobre /minetest/worlds/<worldName>/, para no pisar los mundos singleplayer del
  // usuario. El zip guarda paths RELATIVOS a la carpeta del mundo, así el mismo
  // mundo se puede montar bajo cualquier nombre (portátil entre hosts del realm).
  public async exportRealmWorld(worldName: string): Promise<Uint8Array> {
    if (!this.idbManager) throw new Error('exportRealmWorld: no idbManager');
    await this.executeWorldSync(); // flush de pendientes primero
    const base = '/minetest/worlds/' + worldName + '/';
    const files = await this.idbManager.getAllFiles(base);
    const zipContent: Record<string, Uint8Array> = {};
    for (const f of files) {
      if (!f.path.startsWith(base) || !f.content || !f.content.length) continue;
      const rel = f.path.slice(base.length);
      if (!rel) continue;
      zipContent[rel] = f.content instanceof Uint8Array ? f.content : new Uint8Array(f.content);
    }
    return await new Promise<Uint8Array>((resolve, reject) => {
      zip(zipContent, { level: 6 }, (err, data) => (err ? reject(err) : resolve(data)));
    });
  }

  public async restoreRealmWorld(worldName: string, zipBytes: Uint8Array): Promise<void> {
    if (!this.idbManager) throw new Error('restoreRealmWorld: no idbManager');
    const base = '/minetest/worlds/' + worldName;
    // Borrar lo que haya de ESE mundo (para reflejar borrados del server), sin tocar el resto.
    try { await this.idbManager.deleteAllByPath(base); } catch (e) { /* */ }
    const unzipped = await new Promise<Unzipped>((resolve, reject) => {
      unzip(zipBytes, {}, (err, data) => (err ? reject(err) : resolve(data)));
    });
    const now = Math.floor(Date.now() / 1000);
    const dirs = new Set<string>();
    for (const rel of Object.keys(unzipped)) {
      if (rel.endsWith('/')) continue;
      const full = base + '/' + rel;
      const frags = full.split('/');
      for (let i = 3; i < frags.length; i++) { // crea /minetest/worlds, .../<world>, subdirs
        const dp = frags.slice(0, i).join('/');
        if (dp && !dirs.has(dp)) { await this.idbManager.storeDirectory(dp); dirs.add(dp); }
      }
      const data = unzipped[rel];
      const stats: FileStats = { mtime: now, atime: now, ctime: now, size: data.length, mode: 0 };
      this.fileStats.set(full, stats);
      await this.idbManager.saveFile(full, data, stats);
    }
    this.updateStorageStats();
  }

  // Clear storage
  async clearStorage(area: string = 'all'): Promise<void> {
    if (!this.idbManager) {
      console.error('StorageManager: No IDBManager available for clear operation');
      return;
    }
    
    try {
      if (area === 'all' || area === 'worlds') {
        await this.idbManager.deleteAllByPath(this.WORLDS_SYNC_BASE_PATH);
        this.worldsStats = {
          fileCount: 0,
          totalSize: 0,
          lastUpdate: new Date()
        };
      }
      
      if (area === 'all' || area === 'mods') {
        await this.idbManager.deleteAllByPath(this.MODS_SYNC_BASE_PATH);
        this.modsStats = {
          fileCount: 0,
          totalSize: 0,
          lastUpdate: new Date()
        };
      }
      
      console.log(`StorageManager: Cleared ${area} storage`);
    } catch (e) {
      console.error(`StorageManager: Error clearing ${area} storage:`, e);
    }
  }

  // Force clear all storage
  async forceClearStorage(): Promise<void> {
    if (!this.idbManager) {
      console.error('StorageManager: No IDBManager available for clear operation');
      return;
    }
    
    try {
      await this.idbManager.clearDatabase();
      this.worldsStats = {
        fileCount: 0,
        totalSize: 0,
        lastUpdate: new Date()
      };
      this.modsStats = {
        fileCount: 0,
        totalSize: 0,
        lastUpdate: new Date()
      };
      console.log('StorageManager: Force cleared all storage');
    } catch (e) {
      console.error('StorageManager: Error force clearing storage:', e);
    }
  }

  async getWorlds(): Promise<string[]> {
    if (!this.idbManager) {
      console.error('StorageManager: No IDBManager available for getWorlds operation');
      return [];
    }
    return this.idbManager.getDirectSubDirectories(this.WORLDS_SYNC_BASE_PATH);
  }

  // Borra un mundo entero de IndexedDB (usado por el selector de mundos del loader).
  async deleteWorld(name: string): Promise<void> {
    if (!this.idbManager || this.storagePolicy !== 'indexeddb') return;
    const base = this.WORLDS_SYNC_BASE_PATH + '/' + name;
    try {
      await this.idbManager.deleteAllByPath(base);
      for (const key of Array.from(this.pendingIndexedDBWrites.keys())) {
        if (key.startsWith(base)) this.pendingIndexedDBWrites.delete(key);
      }
      for (const key of Array.from(this.fileStats.keys())) {
        if (key.startsWith(base)) this.fileStats.delete(key);
      }
      this.updateStorageStats();
    } catch (e) {
      console.error('StorageManager: deleteWorld failed for ' + name, e);
    }
  }

  // Ensures a directory exists in IndexedDB
  async ensureDirectoryExists(dirPath: string): Promise<void> {
    if (!this.idbManager || this.storagePolicy !== 'indexeddb') {
      console.log("StorageManager: No IndexedDB manager or not using IndexedDB storage");
      return;
    }
    
    try {
      await this.idbManager.storeDirectory(dirPath);
      console.log(`StorageManager: Ensured directory exists: ${dirPath}`);
    } catch (e) {
      console.error(`StorageManager: Error ensuring directory exists: ${dirPath}`, e);
    }
  }

  // Persists a file to IndexedDB
  async persistFile(filePath: string, content: Uint8Array, stats: FileStats): Promise<void> {
    if (!this.idbManager || this.storagePolicy !== 'indexeddb') {
      console.log("StorageManager: No IndexedDB manager or not using IndexedDB storage");
      return;
    }
    
    return await this.idbManager.saveFile(filePath, content, stats);
  }

  // Removes one uploaded mod's files from IndexedDB (so it doesn't restore on
  // reload). Used by the "Eliminar Mod" button in the Lua mod manager.
  async deleteModFiles(modName: string): Promise<void> {
    if (!this.idbManager || this.storagePolicy !== 'indexeddb') return;
    try {
      await this.idbManager.deleteAllByPath('/minetest/mods/' + modName);
      // Drop any queued writes for that mod too.
      for (const key of Array.from(this.pendingIndexedDBWrites.keys())) {
        if (key.startsWith('/minetest/mods/' + modName)) {
          this.pendingIndexedDBWrites.delete(key);
        }
      }
    } catch (e) {
      console.error('StorageManager: deleteModFiles failed for ' + modName, e);
    }
  }

  // Deletes a directory from IndexedDB
  deleteDirectory(dirPath: string): void {
    if (!this.idbManager || this.storagePolicy !== 'indexeddb') {
      console.log("StorageManager: No IndexedDB manager or not using IndexedDB storage");
      return;
    }
    
    try {
      this.idbManager.deleteDirectory(dirPath);
      console.log(`StorageManager: Deleted directory: ${dirPath}`);
      
      // Update stats if this is a world or mod directory
      if (dirPath.startsWith(this.WORLDS_SYNC_BASE_PATH) || dirPath.startsWith(this.MODS_SYNC_BASE_PATH)) {
        setTimeout(() => {
          this.updateStorageStats();
        }, 1000);
      }
    } catch (e) {
      console.error(`StorageManager: Error deleting directory: ${dirPath}`, e);
    }
  }
} 